import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';

import {
  Button,
  Center,
  Group,
  Image,
  Loader,
  Text,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { IconCircleCheck, IconFileTypePdf } from '@tabler/icons-react';
import { dateToDateString } from '@utils/date';
import { priceToDecimal } from '@utils/price';

import env from '@config/env';

import {
  GetOfferProductQueryParams,
  OffersProductsSortBy,
  useConfirmOfferMutation,
  useGetOfferProductsCountQuery,
  useGetOfferProductsQuery,
  useGetSingleOfferQuery,
} from '@api/offers.api';

import Card from '@components/card/Card';
import { LayoutContextType } from '@components/layout/Layout';
import SearchBar from '@components/SearchBar';

import Forbidden from './Forbidden';
import NotFound from './NotFound';
import { OfferProduct } from '@interfaces/offers.interface';

export default function OfferDetail() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { id } = useParams();

  const [searchParams] = useSearchParams();

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetOfferProductQueryParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 5),
    searchQuery: searchParams.get('search') || undefined,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<OfferProduct>
  >({
    columnAccessor: 'name',
    direction: 'asc',
  });

  useEffect(() => {
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as OffersProductsSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: offer, isLoading, error } = useGetSingleOfferQuery(id!);

  const [confirmOffer, { isLoading: isLoadingConfirmOffer }] =
    useConfirmOfferMutation();

  // Get initial products count
  const {
    data: productsCount = { count: 0 },
    isLoading: isLoadingProductsCount,
  } = useGetOfferProductsCountQuery({ id: id!, searchQuery });

  const { data: productsData = [], isLoading: isProductsLoading } =
    useGetOfferProductsQuery({
      id: id!,
      params: { ...filters, searchQuery },
    });

  // ==========================================================================
  // Handler
  // ==========================================================================
  const handleConfirmClick = async () => {
    try {
      await confirmOffer({ id: id! }).unwrap();
    } catch (e) {
      console.error(e);

      showNotification({
        title: "Errore nella conferma dell'offerta!",
        message: "Impossibile confermare l'offerta.",
        color: 'red',
      });
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: `Dettagli offerta #${id}`,
      backLink: { title: 'Lista offerte', to: '/offerte' },
    });
  }, [id, setLayoutProps]);

  return isLoading || isProductsLoading || isLoadingProductsCount ? (
    <Center my="xl">
      <Loader />
    </Center>
  ) : !isLoading &&
    (!offer || (error && (error as FetchBaseQueryError).status === 404)) ? (
    <NotFound />
  ) : !isLoading &&
    (!offer || (error && (error as FetchBaseQueryError).status === 403)) ? (
    <Forbidden />
  ) : (
    <>
      <Group grow align="start">
        <Card
          title="Informazioni offerta"
          action={{
            title: 'Scarica pdf',
            icon: <IconFileTypePdf />,
            onClick: () => {
              window.open(
                `${env.RESOURCES_URL}/offerte/${offer!.pdfName}`,
                '_blank',
              );
            },
          }}
        >
          <Group justify="space-between">
            <Text fw="bold">Numero offerta</Text>
            <Text>{offer!.id}</Text>
          </Group>
          <Group justify="space-between">
            <Text fw="bold">Data</Text>
            <Text>{dateToDateString(new Date(offer!.date))}</Text>
          </Group>

          {offer!.confirmationDate ? (
            <Text mt="sm">
              <strong>Offerta confermata</strong>
            </Text>
          ) : (
            <Button
              leftSection={<IconCircleCheck />}
              mt="lg"
              onClick={handleConfirmClick}
              loading={isLoadingConfirmOffer}
            >
              Conferma offerta
            </Button>
          )}
        </Card>

        <Card title="Pagamento">
          <Group justify="space-between">
            <Text fw="bold">Tipo di pagamento</Text>
            <Text>
              {offer!.paymentType === 'onlineOnConfirm'
                ? 'Pagamento online'
                : offer!.paymentType === 'bankTransferOnConfirm'
                  ? 'Bonifico bancario'
                  : 'Bonifico bancario'}
            </Text>
          </Group>
          <Group justify="space-between">
            <Text fw="bold">Totale (Iva In.)</Text>
            <Text>€ {priceToDecimal(offer!.totalPrice)}</Text>
          </Group>
        </Card>
      </Group>

      <Title order={3} mt="lg">
        Prodotti
      </Title>
      <Group mt="lg" mb="md">
        <SearchBar
          placeholder="Ricerca per codice o nome prodotto"
          value={filters.searchQuery}
          onChange={(newValue) =>
            setFilters({ ...filters, searchQuery: newValue })
          }
        />
      </Group>

      <DataTable
        striped
        withTableBorder
        fetching={isProductsLoading || isLoadingProductsCount}
        noRecordsText="Nessun prodotto trovato"
        minHeight={productsData.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'imageUrl',
            title: '',
            width: '60px',
            render: (record) => (
              <Image
                src={record.imageUrl}
                alt={record.name}
                h="2rem"
                w="2rem"
                fit="contain"
              />
            ),
          },
          {
            accessor: 'sku',
            title: 'Codice',
            sortable: true,
          },
          {
            accessor: 'name',
            title: 'Nome',
            sortable: true,
          },
          {
            accessor: 'color',
            title: 'Colore',
          },
          {
            accessor: 'size',
            title: 'Taglia',
          },
          {
            accessor: 'quantity',
            title: 'Quantità',
          },
          {
            accessor: 'totalPrice',
            title: 'Prezzo totale',
            render: (record) => (
              <Group gap={3} wrap="nowrap">
                <span>€</span>
                {record.salePercent && (
                  <span>
                    <span style={{ textDecoration: 'line-through' }}>
                      {priceToDecimal(record.totalPrice)}
                    </span>
                  </span>
                )}
                <span>
                  {priceToDecimal(
                    record.totalPrice * (1 - (record.salePercent || 0) / 1000),
                  )}
                </span>
              </Group>
            ),
          },
          {
            accessor: 'salePercent',
            title: 'Sconto',
            render: (record) =>
              record.salePercent ? `${record.salePercent / 10}%` : '-',
          },
        ]}
        records={productsData}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={productsCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[5, 10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
